import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import FocusTrap from 'focus-trap-react';
import styles from './FlyoutModal.module.scss';
import messages from './messages';
import {
  FLYOUT_MODAL_THEME_AOV,
  FLYOUT_MODAL_THEME_CART,
  FLYOUT_MODAL_THEME_REGULAR,
  FLYOUT_MODAL_THEME_TIGHT,
  FLYOUT_MODAL_THEME_GUIDE,
} from './constants';

const FlyoutModal = ({
  isVisible,
  children,
  backButtonLabel,
  title,
  onCloseModal,
  theme,
  showCloseButton,
}) => {
  const { formatMessage } = useIntl();
  const [mouseDownTarget, setMouseDownTarget] = useState();
  const [mouseUpTarget, setMouseUpTarget] = useState();

  const onKeyDown = (event) => {
    if (event.key === 'Escape') {
      onCloseModal();
    }
  };

  useEffect(() => {
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  const onCloseModalCheck = () => {
    if (mouseDownTarget !== mouseUpTarget) {
      return;
    }
    onCloseModal();
  };

  const onMouseUpTest = (e) => {
    setMouseUpTarget(e.target);
  };
  const onMouseDownTest = (e) => {
    setMouseDownTarget(e.target);
  };

  return (
    <FocusTrap>
      <div
        onClick={onCloseModalCheck}
        onMouseUp={onMouseUpTest}
        onMouseDown={onMouseDownTest}
        onKeyDown={onKeyDown}
        tabIndex={0}
        role="button"
        aria-hidden={!isVisible}
        className={classnames(
          styles.modal,
          isVisible ? styles['is-open'] : '',
          styles['modal--flyout'],
        )}
      >
        <div className={styles.modal__overlay} tabIndex="-1" role="button">
          <div
            className={styles.modal__container}
            onKeyDown={onKeyDown}
            tabIndex={0}
            role="button"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            {showCloseButton && (
              <div
                className={classnames(styles.modal__header, {
                  [styles['no-border']]:
                    theme === FLYOUT_MODAL_THEME_AOV ||
                    theme === FLYOUT_MODAL_THEME_CART,
                })}
              >
                {(
                  theme === FLYOUT_MODAL_THEME_GUIDE
                  || theme === FLYOUT_MODAL_THEME_CART
                ) && showCloseButton && (
                  <div className={styles.modal__title}>
                    <h5 className={styles.h5}>{title}</h5>
                    <button
                      className={classnames(styles.btn__close__modal)}
                      aria-label={formatMessage(messages.closeModal)}
                      type="button"
                      onClick={onCloseModal}
                      data-testid="btn__close__modal"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          onCloseModal();
                        }
                      }}
                      id="button1"
                    >
                      <div className={styles.close__button} />
                    </button>
                  </div>
                )}

                {![
                  FLYOUT_MODAL_THEME_CART,
                  FLYOUT_MODAL_THEME_GUIDE,
                  FLYOUT_MODAL_THEME_AOV,
                ].includes(theme) && showCloseButton && (
                  <button
                    className={classnames(
                      styles.modal__close,
                      'icon-before--arrow-small',
                    )}
                    aria-label={formatMessage(messages.closeModal)}
                    type="button"
                    onClick={onCloseModal}
                    data-testid="btn__close__modal"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        onCloseModal();
                      }
                    }}
                  >
                    {backButtonLabel}
                  </button>
                )}
              </div>
            )}

            <div className={classnames(styles.modal__content, styles[theme])}>
              {(theme === FLYOUT_MODAL_THEME_AOV ) && showCloseButton && (
                <button
                  className={classnames(styles.btn__close__modal)}
                  type="button"
                  onClick={onCloseModal}
                  data-testid="btn__close__modal"
                  aria-label={formatMessage(messages.closeModal)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      onCloseModal();
                    }
                  }}
                />
              )}
              {children}
            </div>
          </div>
        </div>
      </div>
    </FocusTrap>
  );
};

FlyoutModal.propTypes = {
  backButtonLabel: PropTypes.string,
  children: PropTypes.node.isRequired,
  isVisible: PropTypes.bool.isRequired,
  onCloseModal: PropTypes.func.isRequired,
  title: PropTypes.string,
  theme: PropTypes.oneOf([
    FLYOUT_MODAL_THEME_REGULAR,
    FLYOUT_MODAL_THEME_TIGHT,
    FLYOUT_MODAL_THEME_AOV,
    FLYOUT_MODAL_THEME_CART,
    FLYOUT_MODAL_THEME_GUIDE,
  ]),
  showCloseButton: PropTypes.bool,
};

FlyoutModal.defaultProps = {
  backButtonLabel: '',
  title: '',
  theme: FLYOUT_MODAL_THEME_REGULAR,
  showCloseButton: true,
};

export default FlyoutModal;
